/**
 * Orphan Folders Resolution Page
 *
 * Manage orphan folders - SharePoint folders without matching Dataverse titles.
 * Allows linking orphans to existing titles or marking them for action.
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileQuestion,
  Link2,
  Trash2,
  Eye,
  AlertCircle,
  CheckCircle2,
  Clock,
  User,
  MessageSquare,
  Hash,
  HardDrive,
  FileStack,
} from "lucide-react";
import {
  getOrphanFolders,
  resolveOrphanFolder,
  formatBytes,
  formatRelativeTime,
  type OrphanFolderRecord,
  type ResolutionStatus,
} from "@/lib/title-folder-api";
import { TitleFolderLayout } from "./layout";

const ITEMS_PER_PAGE = 20;

const RESOLUTION_STATUSES: { value: ResolutionStatus | "all"; label: string; color: string }[] = [
  { value: "all", label: "All Statuses", color: "slate" },
  { value: "Unresolved", label: "Unresolved", color: "amber" },
  { value: "Linked", label: "Linked", color: "emerald" },
  { value: "Deleted", label: "Deleted", color: "red" },
  { value: "Ignored", label: "Ignored", color: "slate" },
];

function getResolutionBadge(status: ResolutionStatus | null) {
  switch (status) {
    case "Linked":
      return (
        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300 border-0">
          <Link2 className="h-3 w-3 mr-1" />
          Linked
        </Badge>
      );
    case "Deleted":
      return (
        <Badge className="bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 border-0">
          <Trash2 className="h-3 w-3 mr-1" />
          Deleted
        </Badge>
      );
    case "Ignored":
      return (
        <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-0">
          <Eye className="h-3 w-3 mr-1" />
          Ignored
        </Badge>
      );
    case "Unresolved":
    default:
      return (
        <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300 border-0">
          <AlertCircle className="h-3 w-3 mr-1" />
          Unresolved
        </Badge>
      );
  }
}

interface ResolveDialogProps {
  orphan: OrphanFolderRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResolve: (titleId: string, notes: string) => Promise<void>;
  resolving: boolean;
}

function ResolveDialog({ orphan, open, onOpenChange, onResolve, resolving }: ResolveDialogProps) {
  const [titleId, setTitleId] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) {
      setTitleId("");
      setNotes("");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titleId.trim()) return;
    await onResolve(titleId.trim(), notes.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-amber-600" />
            Link Orphan to Title
          </DialogTitle>
          <DialogDescription>
            Link this orphan folder to an existing Dataverse title.
          </DialogDescription>
        </DialogHeader>

        {orphan && (
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <FileQuestion className="h-4 w-4 text-amber-500" />
              <span className="font-medium text-slate-800 dark:text-slate-200">
                {orphan.folderName}
              </span>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 space-y-1">
              <p>Bucket: {orphan.bucketFolder ?? "Unknown"}</p>
              {orphan.parsedProjectNumber && (
                <p className="flex items-center gap-1">
                  <Hash className="h-3 w-3" />
                  Parsed project number: <span className="font-mono">{orphan.parsedProjectNumber}</span>
                </p>
              )}
              {orphan.parsedAuthorName && (
                <p className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  Parsed author: {orphan.parsedAuthorName}
                </p>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="titleId">Dataverse Title ID</Label>
            <Input
              id="titleId"
              placeholder="Enter the title GUID..."
              value={titleId}
              onChange={(e) => setTitleId(e.target.value)}
              className="font-mono"
            />
            <p className="text-xs text-slate-500">
              Enter the GUID of the Dataverse title this folder belongs to.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Resolution Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add any notes about this resolution..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!titleId.trim() || resolving}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {resolving ? (
                <>
                  <span className="animate-spin mr-2">⟳</span>
                  Linking...
                </>
              ) : (
                <>
                  <Link2 className="h-4 w-4 mr-2" />
                  Link Folder
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function OrphanFoldersPage() {
  const [orphans, setOrphans] = useState<OrphanFolderRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const { toast } = useToast();

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [resolutionStatus, setResolutionStatus] = useState<ResolutionStatus | "all">("Unresolved");
  const [bucket, setBucket] = useState("");

  // Resolution dialog
  const [selectedOrphan, setSelectedOrphan] = useState<OrphanFolderRecord | null>(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolving, setResolving] = useState(false);

  const requestId = useRef({ id: 0 });
  const loadOrphans = useCallback(async () => {
    const currentRequest = ++requestId.current.id;
    try {
      setLoading(true);

      const params: Parameters<typeof getOrphanFolders>[0] = {
        search: searchQuery.trim() || undefined,
        limit: ITEMS_PER_PAGE,
        offset: page * ITEMS_PER_PAGE,
      };

      if (resolutionStatus !== "all") {
        params.resolutionStatus = resolutionStatus;
      }

      if (bucket.trim()) {
        params.bucketFolder = bucket.trim();
      }

      const result = await getOrphanFolders(params);

      if (currentRequest !== requestId.current.id) return;
      // A resolution or re-audit may remove the last row on the current page.
      if (page > 0 && result.rows.length === 0) {
        setPage(Math.max(0, Math.ceil(result.totalCount / ITEMS_PER_PAGE) - 1));
      }
      setOrphans(result.rows);
      setTotalCount(result.totalCount);
    } catch (error: any) {
      if (currentRequest !== requestId.current.id) return;
      toast({
        title: "Error loading orphan folders",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      if (currentRequest === requestId.current.id) setLoading(false);
    }
  }, [page, resolutionStatus, bucket, searchQuery, toast]);

  useEffect(() => {
    const requests = requestId.current;
    loadOrphans();
    return () => { ++requests.id; };
  }, [loadOrphans]);

  const handleResolve = async (titleId: string, notes: string) => {
    if (!selectedOrphan) return;

    try {
      setResolving(true);
      const result = await resolveOrphanFolder(
        selectedOrphan.id,
        titleId,
        "UI User", // In a real app, this would be the logged-in user
        notes || undefined
      );

      if (result.success) {
        toast({
          title: "Folder linked successfully",
          description: `Orphan folder linked to title ${titleId}`,
        });
        setResolveDialogOpen(false);
        loadOrphans();
      } else {
        throw new Error(result.message);
      }
    } catch (error: any) {
      toast({
        title: "Failed to link folder",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setResolving(false);
    }
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setResolutionStatus("Unresolved");
    setBucket("");
    setPage(0);
  };

  const hasActiveFilters = searchQuery || resolutionStatus !== "Unresolved" || bucket;

  // Stats summary
  const unresolvedCount = orphans.filter((o) => o.resolutionStatus === "Unresolved" || !o.resolutionStatus).length;

  return (
    <TitleFolderLayout onRefresh={loadOrphans} isRefreshing={loading}>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <FileQuestion className="h-6 w-6 text-amber-600" />
              Orphan Folders
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              SharePoint folders without matching Dataverse titles
            </p>
          </div>
          <div className="flex items-center gap-3">
            {unresolvedCount > 0 && (
              <Badge variant="destructive" className="px-3 py-1.5 text-sm font-medium">
                {unresolvedCount} unresolved
              </Badge>
            )}
            <Badge variant="outline" className="text-slate-600 px-3 py-1.5 text-sm font-medium">
              {totalCount.toLocaleString()} total
            </Badge>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex flex-wrap gap-4">
                {/* Search Input */}
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search by folder name, project #, or author..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPage(0);
                      }}
                      className="pl-10 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                    />
                  </div>
                </div>

                {/* Resolution Status Filter */}
                <Select
                  value={resolutionStatus}
                  onValueChange={(value) => {
                    setResolutionStatus(value as ResolutionStatus | "all");
                    setPage(0);
                  }}
                >
                  <SelectTrigger className="w-[180px] bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                    <SelectValue placeholder="Resolution Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {RESOLUTION_STATUSES.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Bucket Filter */}
                <Input
                  placeholder="Bucket (e.g., 1000-1999)"
                  value={bucket}
                  onChange={(e) => {
                    setBucket(e.target.value);
                    setPage(0);
                  }}
                  className="w-[180px] bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                />

                {/* Actions */}
                <div className="flex gap-2">
                  <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white">
                    <Filter className="h-4 w-4 mr-2" />
                    Apply
                  </Button>
                  {hasActiveFilters && (
                    <Button type="button" variant="outline" onClick={clearFilters}>
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Orphan Cards Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-amber-500 border-t-transparent" />
              <p className="mt-3 text-sm text-slate-500">Loading orphan folders...</p>
            </div>
          </div>
        ) : orphans.length === 0 ? (
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/50 mb-6">
                <CheckCircle2 className="h-12 w-12 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-2">
                No Orphan Folders
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-center max-w-md">
                {hasActiveFilters
                  ? "No orphan folders match your current filters. Try adjusting your search criteria."
                  : "All discovered folders have been resolved or linked to their titles."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {orphans.map((orphan) => (
              <Card
                key={orphan.id}
                className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-slate-900/50 transition-all group"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="flex-shrink-0 p-2.5 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                        <FileQuestion className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                          {orphan.folderName}
                        </CardTitle>
                        <CardDescription className="text-xs truncate">
                          {orphan.bucketFolder ?? "Unknown bucket"}
                        </CardDescription>
                      </div>
                    </div>
                    {getResolutionBadge(orphan.resolutionStatus)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Parsed Info */}
                  <div className="space-y-2 text-sm">
                    {orphan.parsedProjectNumber && (
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <Hash className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>Project #:</span>
                        <span className="font-mono font-medium text-slate-800 dark:text-slate-200">
                          {orphan.parsedProjectNumber}
                        </span>
                      </div>
                    )}
                    {orphan.parsedAuthorName && (
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <User className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>Author:</span>
                        <span className="font-medium text-slate-800 dark:text-slate-200">
                          {orphan.parsedAuthorName}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Stats Row */}
                  <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-500">
                    {orphan.fileCount !== null && (
                      <span className="flex items-center gap-1">
                        <FileStack className="h-3.5 w-3.5" />
                        {orphan.fileCount} files
                      </span>
                    )}
                    {orphan.totalSizeBytes !== null && (
                      <span className="flex items-center gap-1">
                        <HardDrive className="h-3.5 w-3.5" />
                        {formatBytes(orphan.totalSizeBytes)}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {formatRelativeTime(orphan.discoveredAtUtc)}
                    </span>
                  </div>

                  {/* Resolution Info (if resolved) */}
                  {orphan.resolutionStatus === "Linked" && orphan.resolvedToTitleId && (
                    <div className="p-2 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg text-xs space-y-1">
                      <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                        <Link2 className="h-3.5 w-3.5" />
                        <span>Linked to: </span>
                        <span className="font-mono truncate">{orphan.resolvedToTitleId}</span>
                      </div>
                      {orphan.resolvedBy && (
                        <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-500">
                          <User className="h-3 w-3" />
                          {orphan.resolvedBy} • {formatRelativeTime(orphan.resolvedUtc)}
                        </div>
                      )}
                      {orphan.resolutionNotes && (
                        <div className="flex items-start gap-2 text-emerald-600 dark:text-emerald-500">
                          <MessageSquare className="h-3 w-3 mt-0.5" />
                          <span className="line-clamp-2">{orphan.resolutionNotes}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                    {orphan.webUrl && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-500 hover:text-slate-700"
                              onClick={() => window.open(orphan.webUrl!, "_blank")}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Open in SharePoint</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {(orphan.resolutionStatus === "Unresolved" || !orphan.resolutionStatus) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-950"
                        onClick={() => {
                          setSelectedOrphan(orphan);
                          setResolveDialogOpen(true);
                        }}
                      >
                        <Link2 className="h-4 w-4 mr-2" />
                        Link to Title
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Showing {page * ITEMS_PER_PAGE + 1} to{" "}
              {Math.min((page + 1) * ITEMS_PER_PAGE, totalCount)} of {totalCount.toLocaleString()}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i;
                  } else if (page < 3) {
                    pageNum = i;
                  } else if (page > totalPages - 4) {
                    pageNum = totalPages - 5 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className={`w-8 ${page === pageNum ? "bg-amber-600 hover:bg-amber-700" : ""}`}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum + 1}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Resolve Dialog */}
        <ResolveDialog
          orphan={selectedOrphan}
          open={resolveDialogOpen}
          onOpenChange={setResolveDialogOpen}
          onResolve={handleResolve}
          resolving={resolving}
        />
      </div>
    </TitleFolderLayout>
  );
}
